import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "../../api/ApiContext";
import { queryKeys } from "../../api/queryKeys";
import {
  absenceListSchema,
  absenceOverviewSchema,
  absenceSchema,
  holidayListSchema,
} from "../../api/schemas";
import type {
  Absence,
  AbsenceOverviewItem,
  AbsenceRequestBody,
  AbsenceStatus,
  Holiday,
} from "../../api/schemas";
import type { DateRange } from "../times/model/period";

/**
 * Loads absences overlapping a range (own, or of an employee for HR administrators).
 *
 * @param range - Inclusive range.
 * @param employeeId - Employee or `null` for the caller.
 * @returns The query result.
 */
export function useAbsences(
  range: DateRange,
  employeeId: string | null = null,
): UseQueryResult<Absence[]> {
  const client = useApiClient();
  const owner = employeeId ?? "me";
  const query = useQuery({
    queryKey: [...queryKeys.absences(range.from, range.to), owner],
    queryFn: ({ signal }) =>
      client.get("/api/v1/absences", absenceListSchema, {
        query: { from: range.from, to: range.to, employeeId: employeeId ?? undefined },
        signal,
      }),
    // Another range of the same person keeps the list while it loads, another person never (F-15).
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[3] === owner ? previous : undefined,
  });

  return query;
}

/**
 * Loads the absences of the employees the caller is responsible for: managers their team, HR
 * administrators everybody (ADR-0110).
 *
 * @param range - Inclusive range (at most 366 days).
 * @param status - Status filter or `null` for all.
 * @param enabled - Whether the caller holds the right `ApproveAbsences`.
 * @returns The query result.
 */
export function useAbsenceOverview(
  range: DateRange,
  status: AbsenceStatus | null,
  enabled: boolean,
): UseQueryResult<AbsenceOverviewItem[]> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: [...queryKeys.allAbsences, "overview", range.from, range.to, status ?? "all"],
    queryFn: ({ signal }) =>
      client.get("/api/v1/absences/overview", absenceOverviewSchema, {
        query: { from: range.from, to: range.to, status: status ?? undefined },
        signal,
      }),
    enabled,
    // The range follows today: at midnight the list stays, and so do open decisions (F-15).
    placeholderData: keepPreviousData,
  });

  return query;
}

/**
 * Invalidates absences and everything derived from them (balances, findings).
 *
 * @returns The invalidation function.
 */
function useInvalidateAbsences(): () => Promise<void> {
  const queryClient = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.allAbsences }),
      queryClient.invalidateQueries({ queryKey: queryKeys.allBalances }),
      queryClient.invalidateQueries({ queryKey: queryKeys.allWorkDays }),
      queryClient.invalidateQueries({ queryKey: queryKeys.allLeaveAccounts }),
      // Education leave changes its quota (F-21).
      queryClient.invalidateQueries({ queryKey: queryKeys.allEducationLeave }),
      // Vacation changes the settlement of every employment relationship (T-166).
      queryClient.invalidateQueries({ queryKey: queryKeys.allLeaveRelationships }),
    ]);
  };

  return invalidate;
}

/**
 * Requests or records an absence.
 *
 * @returns The mutation.
 */
export function useRequestAbsence(): UseMutationResult<Absence, Error, AbsenceRequestBody> {
  const client = useApiClient();
  const invalidate = useInvalidateAbsences();
  const mutation = useMutation({
    mutationFn: (body: AbsenceRequestBody) => client.post("/api/v1/absences", body, absenceSchema),
    onSuccess: invalidate,
  });

  return mutation;
}

/**
 * Actions on an existing absence; `request-cancellation` asks to cancel approved absence (T-167),
 * approve and reject then decide the cancellation.
 */
export type AbsenceAction = "approve" | "reject" | "cancel" | "request-cancellation";

/** Input of {@link useAbsenceAction}. */
export interface AbsenceActionInput {
  readonly id: string;
  readonly action: AbsenceAction;
  /**
   * Reason of HR for approving vacation above the leave account (T-163, `LINV-07`), the required
   * reason of a rejection (T-167, `BURLG-GRANT`), or the reason of HR cancelling the absence of
   * somebody else (T-167).
   */
  readonly reason?: string;
}

/**
 * Approves, rejects, cancels or asks to cancel an absence; an approval may carry the reason of HR, a
 * rejection carries its reason.
 *
 * @returns The mutation.
 */
export function useAbsenceAction(): UseMutationResult<Absence, Error, AbsenceActionInput> {
  const client = useApiClient();
  const invalidate = useInvalidateAbsences();
  const mutation = useMutation({
    mutationFn: (input: AbsenceActionInput) =>
      client.post(
        `/api/v1/absences/${encodeURIComponent(input.id)}:${input.action}`,
        input.reason === undefined ? undefined : { reason: input.reason },
        absenceSchema,
      ),
    onSuccess: invalidate,
  });

  return mutation;
}

/**
 * Loads the public holidays of the caller's establishments in a range.
 *
 * @param range - Inclusive range (at most 366 days).
 * @returns The query result.
 */
export function useHolidays(range: DateRange): UseQueryResult<Holiday[]> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: ["me", "holidays", range.from, range.to],
    queryFn: ({ signal }) =>
      client.get("/api/v1/me/holidays", holidayListSchema, {
        query: { from: range.from, to: range.to },
        signal,
      }),
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });

  return query;
}
